import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { getMessaging, getToken, onMessage } from "firebase/messaging";
import { app } from '../firebase';

const DEFAULT_ICON = '/krinkels-icon-192.png';
const DEFAULT_ANDROID_CHANNEL_ID = 'krinkels-alerts';
let androidChannelReady = false;

const getNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform() ? Capacitor.getPlatform() : 'web';
  } catch {
    return 'web';
  }
};

const isNativeMobile = () => {
  const platform = getNativePlatform();
  return platform === 'android' || platform === 'ios';
};

const isNativeAndroid = () => getNativePlatform() === 'android';

const normalizePermissionState = (value) => {
  if (value === 'granted') return 'granted';
  if (value === 'denied') return 'denied';
  return 'default';
};

const ensureAndroidNotificationChannel = async () => {
  if (!isNativeAndroid() || androidChannelReady) return;

  try {
    await LocalNotifications.createChannel({
      id: DEFAULT_ANDROID_CHANNEL_ID,
      name: 'Krinkels meldingen',
      description: 'Belangrijke systeemmeldingen van Krinkels Robot Beheer',
      importance: 5,
      visibility: 1,
      vibration: true,
      lights: true
    });
    androidChannelReady = true;
  } catch (error) {
    const message = String(error?.message || '').toLowerCase();
    if (message.includes('exist') || message.includes('already')) {
      androidChannelReady = true;
      return;
    }
    console.warn('Android notification channel warning:', error);
  }
};

// Request notification permission
export const requestNotificationPermission = async () => {
  try {
    if (isNativeMobile()) {
      if (isNativeAndroid()) {
        await ensureAndroidNotificationChannel();
      }
      const current = await LocalNotifications.checkPermissions().catch(() => ({ display: 'prompt' }));
      let permission = current?.display || current?.receive || 'prompt';

      if (permission === 'prompt') {
        const requested = await LocalNotifications.requestPermissions().catch(() => ({ display: 'denied' }));
        permission = requested?.display || requested?.receive || 'denied';
      }

      const granted = permission === 'granted';
      console.log(granted ? '✅ Native Android notification permission granted' : '❌ Native Android notification permission denied');
      return granted;
    }

    if (typeof Notification === 'undefined') {
      return false;
    }

    const permission = await Notification.requestPermission();

    if (permission === 'granted') {
      console.log('✅ Notification permission granted');
      return true;
    } else {
      console.log('❌ Notification permission denied');
      return false;
    }
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

export const getNotificationPermissionState = async () => {
  try {
    if (isNativeMobile()) {
      const current = await LocalNotifications.checkPermissions().catch(() => ({ display: 'prompt' }));
      return normalizePermissionState(current?.display || current?.receive || 'prompt');
    }

    if (typeof Notification === 'undefined') {
      return 'unsupported';
    }

    return normalizePermissionState(Notification.permission);
  } catch {
    return 'default';
  }
};

export const clearSystemNotification = async (notificationId) => {
  const id = Number(notificationId);
  if (!Number.isFinite(id)) return;

  if (isNativeMobile()) {
    try {
      await LocalNotifications.cancel({ notifications: [{ id }] });
    } catch {
      void 0;
    }

    try {
      await LocalNotifications.removeDeliveredNotifications({ notifications: [{ id }] });
    } catch {
      void 0;
    }
  }
};

// Get FCM token
export const getFCMToken = async () => {
  try {
    if (isNativeMobile() || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return null;
    }

    const registration = await navigator.serviceWorker.ready;
    if (!registration) {
      console.log('ℹ️ Service Worker not ready');
      return null;
    }

    const messaging = getMessaging(app);
    const vapidKey = process.env.REACT_APP_VAPID_KEY;
    if (!vapidKey || vapidKey === 'YOUR_VAPID_KEY_HERE') {
      return null;
    }
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration
    });

    if (token) {
      console.log('✅ FCM Token generated successfully');
      return token;
    }
  } catch (error) {
    console.log('ℹ️ FCM not available:', error.message);
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  if (isNativeMobile()) {
    return () => {};
  }

  const messaging = getMessaging(app);

  return onMessage(messaging, (payload) => {
    console.log('📬 Foreground message received:', payload);
    callback(payload);
  });
};

// Send browser or native notification
export const sendBrowserNotification = async (title, body, icon = DEFAULT_ICON, options = {}) => {
  const safeTitle = String(title || 'Nieuwe melding');
  const safeBody = String(body || '');

  if (isNativeMobile()) {
    const granted = await requestNotificationPermission();
    if (!granted) {
      return null;
    }

    if (isNativeAndroid()) {
      await ensureAndroidNotificationChannel();
    }

    const id = Number.isFinite(Number(options.id))
      ? Number(options.id)
      : Math.floor(Date.now() % 2000000000);

    const notificationConfig = {
      id,
      title: safeTitle,
      body: safeBody,
      largeBody: safeBody,
      sound: 'default',
      extra: options.data || {}
    };

    if (isNativeAndroid()) {
      Object.assign(notificationConfig, {
        channelId: options.channelId || DEFAULT_ANDROID_CHANNEL_ID,
        ongoing: options.ongoing === true,
        autoCancel: options.autoCancel !== false,
        summaryText: options.summaryText || 'Krinkels Robot Beheer'
      });
    }

    try {
      await LocalNotifications.schedule({
        notifications: [notificationConfig]
      });
      return { id };
    } catch (error) {
      console.error('Native mobile notification failed:', error);
      return null;
    }
  }

  if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification(safeTitle, {
      body: safeBody,
      icon,
      badge: icon,
      tag: options.tag || 'melding-notification',
      requireInteraction: options.requireInteraction === true
    });

    if (options.autoCloseMs !== false) {
      window.setTimeout(() => {
        try {
          notification.close();
        } catch {
          void 0;
        }
      }, Number(options.autoCloseMs || 5000));
    }

    return notification;
  }

  return null;
};
